import useSWR from "swr";

import { useCallback, useEffect, useMemo, useState } from "react";

import Button from "components/Button";
import Input from "components/Input";
import ToolDropdownSelector, {
  CUSTOM_OPTION,
} from "components/ToolDropdownSelector";
import ToolRangeSelector from "components/ToolRangeSelector";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

export const getCountriesUrl = () => {
  const url = new ApiEndpoint("/api/v2/countries?sort=name");
  return url.href;
};

export const getSectorsUrl = () => {
  const url = new ApiEndpoint("/api/v2/sectors?sort=name");
  return url.href;
};

export default function Filters({ onChange }: any) {
  const { data: rawCountries = [] } = useSWR(getCountriesUrl(), baseFetcher);
  const { data: rawSectors = [] } = useSWR(getSectorsUrl(), baseFetcher);

  const [filters, setFilters] = useState<any>({});
  const [marketCapValue, setMarketCapValue] = useState([0, 999999999]);

  const countries = useMemo(() => {
    return (
      rawCountries?.map((value: any) => ({
        label: value.name,
        value: value.isoAlpha2,
      })) || []
    );
  }, [rawCountries]);
  const sectors = useMemo(() => {
    return (
      rawSectors?.map((value: any) => ({
        label: value.name,
        value: value.name,
      })) || []
    );
  }, [rawSectors]);

  const marketCap = useMemo(
    () => [
      {
        value: undefined,
        label: "Any",
        valueLabel: "Any",
      },
      {
        value: "0,5000",
        label: "Small & Mid Cap",
        valueLabel: "< 5000",
      },
      {
        value: "5000,9999999999",
        label: "Large Market Cap",
        valueLabel: "> 5000",
      },
      {
        value: CUSTOM_OPTION,
        label: "Custom",
        valueLabel: "Customized",
      },
    ],
    []
  );

  const onSelectCountry = useCallback(
    (selected) => {
      const curFilters = { ...filters };
      const selectedValue = selected.map((item: any) => item.value);
      curFilters["isoCountry"] = selectedValue.join(",");
      setFilters(curFilters);
    },
    [filters]
  );

  const onSelectSector = useCallback(
    (selected) => {
      const curFilters = { ...filters };
      const selectedValue = selected.map((item: any) => item.value);

      curFilters["sector"] = selectedValue.join(",");
      setFilters(curFilters);
    },
    [filters]
  );

  const onSelectMarketCap = useCallback(
    (selected) => {
      const curFilters = { ...filters };
      curFilters["market_cap"] = selected.value;

      setFilters(curFilters);
    },
    [filters]
  );

  const marketCapCustomFooter = useMemo(() => {
    return (
      <div className="p-[10px]">
        <div className="">
          <label>Min</label>
          <Input
            inputClassName="w-full rounded"
            type="number"
            defaultValue={marketCapValue[0]}
            onChange={(e) => {
              const value = [Number(e.target.value), marketCapValue[1]];
              setMarketCapValue(value);
              onSelectMarketCap({ value: value.join(",") });
            }}
          />
        </div>
        <div className="mt-[10px]">
          <label>Max</label>
          <Input
            inputClassName="w-full rounded"
            type="number"
            defaultValue={marketCapValue[1]}
            onChange={(e) => {
              const value = [marketCapValue[0], Number(e.target.value)];
              setMarketCapValue(value);
              onSelectMarketCap({ value: value.join(",") });
            }}
          />
        </div>
      </div>
    );
  }, [marketCapValue, onSelectMarketCap]);

  const onValueChange = useCallback(
    (min, max, key) => {
      const curFilters = { ...filters };
      curFilters[key] = [min, max].join(",");
      setFilters(curFilters);
    },
    [filters]
  );

  useEffect(() => {
    onChange?.(filters);
  }, [filters, onChange]);

  return (
    <div className="flex flex-col relative">
      {Object.keys(filters).length > 0 && (
        <div className="p-[4px] flex min-w-[150px] absolute right-0 top-[-50px]">
          <Button
            onClick={() => setFilters({})}
            className="!bg-smart-teal !ml-0 !mr-0 w-full"
          >
            Clear All Filters
          </Button>
        </div>
      )}
      <div className="flex p-[4px] gap-2 mb-[10px] overflow-auto">
        <ToolDropdownSelector
          label="Country"
          options={countries}
          selected={countries}
          multiple
          filterKey="isoCountry"
          filters={filters}
          onSelect={onSelectCountry}
        />
        <ToolDropdownSelector
          label="Sector"
          options={sectors}
          selected={sectors}
          multiple
          filterKey="sector"
          filters={filters}
          onSelect={onSelectSector}
        />
        <ToolDropdownSelector
          label="Market Cap"
          options={marketCap}
          filterKey="market_cap"
          selected={marketCap}
          onSelect={onSelectMarketCap}
          filters={filters}
          footer={marketCapCustomFooter}
        />
        <ToolRangeSelector
          label="SmartScore"
          min={0}
          max={5}
          filterKey="country-and-sector-score"
          onValueChange={onValueChange}
          filters={filters}
        />
        <ToolRangeSelector
          label="Value"
          min={0}
          max={5}
          filterKey="country-and-sector-value"
          filters={filters}
          onValueChange={onValueChange}
        />
        <ToolRangeSelector
          label="Dividend"
          min={0}
          max={5}
          filterKey="country-and-sector-dividend"
          filters={filters}
          onValueChange={onValueChange}
        />
        <ToolRangeSelector
          label="Growth"
          min={0}
          max={5}
          filterKey="country-and-sector-growth"
          filters={filters}
          onValueChange={onValueChange}
        />
        <ToolRangeSelector
          label="Resilience"
          min={0}
          max={5}
          filterKey="country-and-sector-resilience"
          filters={filters}
          onValueChange={onValueChange}
        />
        <ToolRangeSelector
          label="Momentum"
          min={0}
          max={5}
          filterKey="country-and-sector-momentum"
          filters={filters}
          onValueChange={onValueChange}
        />
      </div>
    </div>
  );
}
